import { Box, ClickAwayListener, Popper } from "@mui/material";
import type { JSX, KeyboardEvent } from "react";
import { useRef, useState } from "react";
import { ChromePicker, ColorResult } from "react-color";

import { colors } from "@/theme/tokens/colors";

const styles = {
  mainContainer: {
    height: "55px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    border: `1px dashed ${colors.brand.blue}`,
    borderRadius: "12px",
    overflow: "hidden",
    cursor: "pointer",
  },
  colorPreview: {
    width: "20%",
    height: "100%",
  },
  hexValue: {
    width: "80%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#535862",
  },
};

interface ColorPickerProps {
  color: string;
  onColorChange: (color: string) => void;
}

export const ColorPicker = ({ color, onColorChange }: ColorPickerProps): JSX.Element => {
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const handleClick = (): void => {
    setDisplayColorPicker((isDisplayed) => !isDisplayed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  const handleClose = (): void => {
    setDisplayColorPicker(false);
  };

  const handleColorChange = (color: ColorResult): void => {
    onColorChange(color.hex);
  };

  return (
    <>
      <Box
        ref={pickerRef}
        sx={styles.mainContainer}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <Box sx={{ ...styles.colorPreview, backgroundColor: color }} />
        <Box sx={styles.hexValue}>{color}</Box>
      </Box>
      <Popper open={displayColorPicker} anchorEl={pickerRef.current} placement="top-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <ChromePicker color={color} disableAlpha onChange={handleColorChange} />
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
